import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import ProgressResetTool from '../../components/admin/progress-reset/ProgressResetTool';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { AssignUsersDialog } from '../../components/course-builder/AssignUsersDialog';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Plus,
  ArrowLeft,
  Save,
  Settings,
  Eye,
  Globe,
  GlobeLock,
  Loader2,
  Trash2,
  Edit,
  FileVideo,
  FileText,
  Link as LinkIcon,
  Upload,
  GripVertical,
  ChevronDown,
  ChevronRight,
  BookOpen,
  FolderOpen,
  Sparkles,
  Check,
  X,
  Video,
  File,
  ExternalLink,
  Users,
  UserPlus,
  FileAudio,
  Search
} from 'lucide-react';

export default function CourseEditor() {
  const { courseId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);
  
  const [course, setCourse] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('structure');
  
  // Module dialogs
  const [showModuleDialog, setShowModuleDialog] = useState(false);
  const [showSubModuleDialog, setShowSubModuleDialog] = useState(false);
  const [showContentDialog, setShowContentDialog] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [editingSubModule, setEditingSubModule] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null); // For content upload
  
  // Form data
  const [moduleForm, setModuleForm] = useState({ title: '', description: '', has_sub_modules: true });
  const [subModuleForm, setSubModuleForm] = useState({ title: '', description: '' });
  const [contentForm, setContentForm] = useState({ type: 'video_url', url: '', title: '', description: '', transcript: '' });
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  
  // Quiz generation
  const [generatingQuiz, setGeneratingQuiz] = useState(false);
  const [showQuizPreview, setShowQuizPreview] = useState(false);
  const [quizPreviewData, setQuizPreviewData] = useState(null);
  const [showQuizOptionsDialog, setShowQuizOptionsDialog] = useState(false);
  const [quizTarget, setQuizTarget] = useState(null);
  const [quizOptions, setQuizOptions] = useState({
    num_questions: 7,
    include_mcq: true,
    include_true_false: true,
    include_short_answer: true,
    include_coding: true,
    difficulty_mix: { easy: 2, medium: 3, hard: 2 }
  });
  const [publishingQuiz, setPublishingQuiz] = useState(false);
  
  // User Assignment (department filter + pick/assign-everyone live in AssignUsersDialog)
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  
  // Transcript extraction
  const [transcribing, setTranscribing] = useState(null); // video_id being transcribed
  
  // Content reorder tracking (local changes before save)
  const [pendingReorders, setPendingReorders] = useState({}); // { targetId: { targetType, newOrder } }
  const [localContentOrder, setLocalContentOrder] = useState({}); // { targetId: [orderedContent] }
  
  // Edit Quiz state
  const [showEditQuizDialog, setShowEditQuizDialog] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [editingQuestions, setEditingQuestions] = useState([]);
  const [savingQuiz, setSavingQuiz] = useState(false);
  
  // Transcript editing state
  const [showTranscriptDialog, setShowTranscriptDialog] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [transcriptText, setTranscriptText] = useState('');
  const [savingTranscript, setSavingTranscript] = useState(false);
  
  // Multi-select deletion state
  const [selectedContent, setSelectedContent] = useState({}); // { targetId: Set of content ids }
  const [deletingSelected, setDeletingSelected] = useState(false);
  
  // Accordion expansion state - tracks which modules and submodules are expanded
  const [expandedModules, setExpandedModules] = useState([]);
  const [expandedSubModules, setExpandedSubModules] = useState({});

  // silent: refresh without the full-page spinner so open dialogs (Assign Users) keep their state
  const fetchCourse = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const response = await api.get(`/tech-learning/builder/courses/${courseId}`);
      setCourse(response.data.course);
    } catch (error) {
      console.error('Failed to fetch course:', error);
      toast.error('Failed to load course');
      navigate('/admin/course-builder');
    } finally {
      setLoading(false);
    }
  }, [api, courseId, navigate]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get('/tech-learning/builder/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchCourse();
    fetchCategories();
  }, [fetchCourse, fetchCategories]);

  // Auto-expand modules when course loads (but preserve user's manual expansion/collapse choices)
  useEffect(() => {
    if (course?.modules && expandedModules.length === 0) {
      // Only auto-expand on initial load when nothing is expanded
      // This preserves user's choices after content operations
      setExpandedModules(course.modules.map(m => m.id));
    }
  }, [course?.modules?.length]); // Only run when module count changes

  // Course Settings
  const handleSaveCourse = async () => {
    try {
      setSaving(true);
      
      // Save course metadata
      await api.put(`/tech-learning/builder/courses/${courseId}`, {
        title: course.title,
        description: course.description,
        category_id: course.category_id,
        passing_score: course.passing_score,
        tags: course.tags
      });
      
      // Save any pending content reorders
      const reorderPromises = Object.entries(pendingReorders).map(([targetId, data]) => {
        return api.put(
          `/tech-learning/builder/content/reorder?target_type=${data.targetType}&target_id=${targetId}`,
          data.newOrder
        );
      });
      
      if (reorderPromises.length > 0) {
        await Promise.all(reorderPromises);
        setPendingReorders({});
        setLocalContentOrder({});
      }
      
      toast.success('Course saved');
      fetchCourse(); // Refresh to get latest data
    } catch (error) {
      console.error('Failed to save course:', error);
      toast.error('Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      if (course.status === 'published') {
        await api.post(`/tech-learning/builder/courses/${courseId}/unpublish`);
        toast.success('Course unpublished');
      } else {
        await api.post(`/tech-learning/builder/courses/${courseId}/publish`);
        toast.success('Course published!');
      }
      fetchCourse();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.errors) {
        toast.error(detail.errors.join(', '));
      } else {
        toast.error(detail || 'Failed to update course');
      }
    }
  };

  // Module CRUD
  const handleCreateModule = async () => {
    if (!moduleForm.title.trim()) {
      toast.error('Module title is required');
      return;
    }
    try {
      await api.post(`/tech-learning/builder/courses/${courseId}/modules`, moduleForm);
      toast.success('Module created');
      setShowModuleDialog(false);
      setModuleForm({ title: '', description: '', has_sub_modules: true });
      fetchCourse();
    } catch (error) {
      toast.error('Failed to create module');
    }
  };

  const handleUpdateModule = async () => {
    if (!editingModule) return;
    try {
      await api.put(`/tech-learning/builder/modules/${editingModule.id}`, {
        title: moduleForm.title,
        description: moduleForm.description
      });
      toast.success('Module updated');
      setEditingModule(null);
      setShowModuleDialog(false);
      setModuleForm({ title: '', description: '', has_sub_modules: true });
      fetchCourse();
    } catch (error) {
      toast.error('Failed to update module');
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!confirm('Delete this module and all its content?')) return;
    try {
      await api.delete(`/tech-learning/builder/modules/${moduleId}`);
      toast.success('Module deleted');
      fetchCourse();
    } catch (error) {
      toast.error('Failed to delete module');
    }
  };

  // Sub-Module CRUD
  const handleCreateSubModule = async () => {
    if (!subModuleForm.title.trim() || !selectedTarget) {
      toast.error('Sub-module title is required');
      return;
    }
    try {
      await api.post(`/tech-learning/builder/modules/${selectedTarget.id}/submodules`, subModuleForm);
      toast.success('Sub-module created');
      setShowSubModuleDialog(false);
      setSubModuleForm({ title: '', description: '' });
      setSelectedTarget(null);
      fetchCourse();
    } catch (error) {
      toast.error('Failed to create sub-module');
    }
  };

  const handleDeleteSubModule = async (subModuleId) => {
    if (!confirm('Delete this sub-module and all its content?')) return;
    try {
      await api.delete(`/tech-learning/builder/submodules/${subModuleId}`);
      toast.success('Sub-module deleted');
      fetchCourse();
    } catch (error) {
      toast.error('Failed to delete sub-module');
    }
  };

  // Content Management
  const handleAddContent = async () => {
    if (!selectedTarget || !contentForm.url.trim() || !contentForm.title.trim()) {
      toast.error('URL and title are required');
      return;
    }
    
    try {
      const targetType = selectedTarget.module_id ? 'submodule' : 'module';
      
      if (contentForm.type === 'video_url') {
        let apiUrl = `/tech-learning/builder/content/video-url?target_type=${targetType}&target_id=${selectedTarget.id}&url=${encodeURIComponent(contentForm.url)}&title=${encodeURIComponent(contentForm.title)}`;
        if (contentForm.transcript && contentForm.transcript.trim()) {
          apiUrl += `&transcript=${encodeURIComponent(contentForm.transcript.trim())}`;
        }
        await api.post(apiUrl);
      } else if (contentForm.type === 'onedrive') {
        let apiUrl = `/tech-learning/builder/content/onedrive-link?target_type=${targetType}&target_id=${selectedTarget.id}&url=${encodeURIComponent(contentForm.url)}&title=${encodeURIComponent(contentForm.title)}&description=${encodeURIComponent(contentForm.description || '')}`;
        if (contentForm.transcript && contentForm.transcript.trim()) {
          apiUrl += `&transcript=${encodeURIComponent(contentForm.transcript.trim())}`;
        }
        await api.post(apiUrl);
      }
      
      toast.success('Content added');
      setShowContentDialog(false);
      setContentForm({ type: 'video_url', url: '', title: '', description: '', transcript: '' });
      setSelectedTarget(null);
      fetchCourse();
    } catch (error) {
      console.error('Failed to add content:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to add content';
      toast.error(errorMessage);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTarget) return;
    
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video must be less than 50MB');
      return;
    }
    
    const targetType = selectedTarget.module_id ? 'submodule' : 'module';
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('target_type', targetType);
      formData.append('target_id', selectedTarget.id);
      formData.append('title', file.name);
      
      await api.post('/tech-learning/builder/upload/video', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Video uploaded');
      fetchCourse();
    } catch (error) {
      console.error('Failed to upload video:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to upload video';
      toast.error(errorMessage);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDocumentUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selectedTarget) return;
    
    const targetType = selectedTarget.module_id ? 'submodule' : 'module';
    
    try {
      setUploading(true);
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      formData.append('target_type', targetType);
      formData.append('target_id', selectedTarget.id);
      
      const response = await api.post('/tech-learning/builder/upload/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${response.data.uploaded_count} document(s) uploaded`);
      fetchCourse();
    } catch (error) {
      toast.error('Failed to upload documents');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteContent = async (contentType, contentId, target) => {
    if (!confirm('Delete this content?')) return;
    const targetType = target.module_id ? 'submodule' : 'module';
    
    try {
      await api.delete(
        `/tech-learning/builder/content/${contentType}/${contentId}?target_type=${targetType}&target_id=${target.id}`
      );
      toast.success('Content deleted');
      fetchCourse();
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  // Multi-select content functions
  const toggleContentSelection = (targetId, contentId) => {
    setSelectedContent(prev => {
      const targetSelected = new Set(prev[targetId] || []);
      if (targetSelected.has(contentId)) {
        targetSelected.delete(contentId);
      } else {
        targetSelected.add(contentId);
      }
      return { ...prev, [targetId]: targetSelected };
    });
  };

  const toggleSelectAll = (target, allContent) => {
    const targetId = target.id;
    const currentSelected = selectedContent[targetId] || new Set();
    const allIds = allContent.map(item => item.id);
    
    if (currentSelected.size === allContent.length) {
      // Deselect all
      setSelectedContent(prev => ({ ...prev, [targetId]: new Set() }));
    } else {
      // Select all
      setSelectedContent(prev => ({ ...prev, [targetId]: new Set(allIds) }));
    }
  };

  const getSelectedCount = (targetId) => {
    return (selectedContent[targetId] || new Set()).size;
  };

  const handleDeleteSelected = async (target, allContent) => {
    const targetId = target.id;
    const selected = selectedContent[targetId] || new Set();
    
    if (selected.size === 0) {
      toast.error('No items selected');
      return;
    }
    
    if (!confirm(`Delete ${selected.size} selected item(s)?`)) return;
    
    setDeletingSelected(true);
    const targetType = target.module_id ? 'submodule' : 'module';
    
    try {
      // Delete items one by one (could be optimized with bulk endpoint later)
      const selectedItems = allContent.filter(item => selected.has(item.id));
      
      for (const item of selectedItems) {
        await api.delete(
          `/tech-learning/builder/content/${item.contentType}/${item.id}?target_type=${targetType}&target_id=${target.id}`
        );
      }
      
      toast.success(`${selected.size} item(s) deleted`);
      setSelectedContent(prev => ({ ...prev, [targetId]: new Set() }));
      fetchCourse();
    } catch (error) {
      console.error('Failed to delete selected items:', error);
      toast.error('Failed to delete some items');
      fetchCourse(); // Refresh anyway to show partial results
    } finally {
      setDeletingSelected(false);
    }
  };

  // AI Quiz Generation
  const handleOpenQuizOptions = (target) => {
    setQuizTarget(target);
    setShowQuizOptionsDialog(true);
  };

  const handleGenerateQuiz = async () => {
    if (!quizTarget) return;
    const target = quizTarget;
    const targetType = target.module_id ? 'submodule' : 'module';
    
    try {
      setGeneratingQuiz(true);
      setShowQuizOptionsDialog(false);
      
      const response = await api.post('/tech-learning/builder/ai/generate-quiz', {
        target_type: targetType,
        target_id: target.id,
        num_questions: quizOptions.num_questions,
        include_mcq: quizOptions.include_mcq,
        include_true_false: quizOptions.include_true_false,
        include_short_answer: quizOptions.include_short_answer,
        include_coding: quizOptions.include_coding,
        difficulty_mix: quizOptions.difficulty_mix
      });
      
      setQuizPreviewData({
        ...response.data,
        target,
        targetType
      });
      setShowQuizPreview(true);
      toast.success(`Generated ${response.data.questions_generated} questions`);
      fetchCourse();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to generate quiz');
    } finally {
      setGeneratingQuiz(false);
      setQuizTarget(null);
    }
  };

  // Publish Quiz
  const handlePublishQuiz = async () => {
    if (!quizPreviewData?.assessment_id) return;
    
    try {
      setPublishingQuiz(true);
      await api.patch(`/tech-learning/builder/ai/assessments/${quizPreviewData.assessment_id}/publish`);
      toast.success('Quiz published successfully! Learners can now take it.');
      setShowQuizPreview(false);
      setQuizPreviewData(null);
      fetchCourse();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to publish quiz');
    } finally {
      setPublishingQuiz(false);
    }
  };

  // Edit Quiz Functions
  const handleOpenEditQuiz = async (target) => {
    if (!target.assessment_id) {
      toast.error('No quiz found for this module');
      return;
    }
    
    try {
      const response = await api.get(`/tech-learning/builder/ai/assessments/${target.assessment_id}`);
      const assessment = response.data.assessment;
      setEditingQuiz(assessment);
      setEditingQuestions(JSON.parse(JSON.stringify(assessment.questions || []))); // Deep copy
      setShowEditQuizDialog(true);
    } catch (error) {
      toast.error('Failed to load quiz for editing');
    }
  };

  const handleSaveQuiz = async () => {
    if (!editingQuiz) return;
    
    try {
      setSavingQuiz(true);
      await api.put(`/tech-learning/builder/ai/assessments/${editingQuiz.id}`, {
        questions: editingQuestions
      });
      toast.success('Quiz updated successfully');
      setShowEditQuizDialog(false);
      setEditingQuiz(null);
      setEditingQuestions([]);
      fetchCourse();
    } catch (error) {
      toast.error('Failed to save quiz');
    } finally {
      setSavingQuiz(false);
    }
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...editingQuestions];
    updated[index] = { ...updated[index], [field]: value };
    setEditingQuestions(updated);
  };

  const updateQuestionOption = (questionIndex, optionIndex, field, value) => {
    const updated = [...editingQuestions];
    const options = [...(updated[questionIndex].options || [])];
    options[optionIndex] = { ...options[optionIndex], [field]: value };
    updated[questionIndex] = { ...updated[questionIndex], options };
    setEditingQuestions(updated);
  };

  const deleteQuestion = (index) => {
    const updated = editingQuestions.filter((_, i) => i !== index);
    setEditingQuestions(updated);
  };

  // Transcript functions - works for videos AND onedrive links
  const handleOpenTranscriptDialog = (item, targetId, targetType) => {
    setEditingVideo({ ...item, targetId, targetType });
    setTranscriptText(item.transcript || '');
    setShowTranscriptDialog(true);
  };

  const handleSaveTranscript = async () => {
    if (!editingVideo) return;
    
    try {
      setSavingTranscript(true);
      
      // Determine endpoint based on content type
      const isOneDrive = editingVideo.contentType === 'onedrive_link' || editingVideo.type === 'onedrive_link';
      const endpoint = isOneDrive 
        ? `/tech-learning/builder/content/onedrive-link/${editingVideo.id}/transcript`
        : `/tech-learning/builder/content/video/${editingVideo.id}/transcript`;
      
      await api.put(endpoint, {
        transcript: transcriptText,
        target_id: editingVideo.targetId,
        target_type: editingVideo.targetType
      });
      toast.success('Transcript saved successfully');
      setShowTranscriptDialog(false);
      setEditingVideo(null);
      setTranscriptText('');
      fetchCourse(); // Refresh to show updated data
    } catch (error) {
      console.error('Failed to save transcript:', error);
      toast.error('Failed to save transcript');
    } finally {
      setSavingTranscript(false);
    }
  };

  const handleOpenAssignDialog = () => setShowAssignDialog(true);

  // Transcript Extraction
  const handleTranscribeVideo = async (video, target) => {
    if (video.type !== 'video_upload') {
      toast.error('Can only transcribe uploaded videos');
      return;
    }
    
    const targetType = target.module_id ? 'submodule' : 'module';
    
    try {
      setTranscribing(video.id);
      const response = await api.post(
        `/tech-learning/builder/transcribe-video/${video.id}?target_type=${targetType}&target_id=${target.id}`
      );
      toast.success('Video transcribed successfully');
      fetchCourse();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transcribe video');
    } finally {
      setTranscribing(null);
    }
  };

  // Render content list
  // Handle content reordering - now only updates local state, saved on Save button
  const updateLocalContentOrder = (target, newOrderedContent) => {
    const targetType = target.module_id ? 'submodule' : 'module';
    const targetId = target.id;
    
    // Store the new order for saving later
    const newOrder = newOrderedContent.map((c, idx) => ({
      id: c.id,
      type: c.contentType,
      order: idx + 1
    }));
    
    setPendingReorders(prev => ({
      ...prev,
      [targetId]: { targetType, newOrder }
    }));
    
    setLocalContentOrder(prev => ({
      ...prev,
      [targetId]: newOrderedContent
    }));
  };

  const moveContentUp = (target, item, allContent) => {
    const currentIndex = allContent.findIndex(c => c.id === item.id);
    if (currentIndex <= 0) return;
    
    // Create new array with swapped items
    const newContent = [...allContent];
    [newContent[currentIndex - 1], newContent[currentIndex]] = [newContent[currentIndex], newContent[currentIndex - 1]];
    
    // Update local state only (no API call)
    updateLocalContentOrder(target, newContent);
  };

  const moveContentDown = (target, item, allContent) => {
    const currentIndex = allContent.findIndex(c => c.id === item.id);
    if (currentIndex >= allContent.length - 1) return;
    
    // Create new array with swapped items
    const newContent = [...allContent];
    [newContent[currentIndex], newContent[currentIndex + 1]] = [newContent[currentIndex + 1], newContent[currentIndex]];
    
    // Update local state only (no API call)
    updateLocalContentOrder(target, newContent);
  };

  const renderContentList = (target) => {
    const content = target.content || {};
    const videos = (content.videos || []).map(v => ({ ...v, contentType: 'video' }));
    const documents = (content.documents || []).map(d => ({ ...d, contentType: 'document' }));
    const links = (content.onedrive_links || []).map(l => ({ ...l, contentType: 'onedrive_link' }));
    
    // Use local order if available (pending changes), otherwise use server order
    let allContent;
    if (localContentOrder[target.id]) {
      allContent = localContentOrder[target.id];
    } else {
      // Combine all content and sort by order
      allContent = [...videos, ...documents, ...links].sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    
    const hasUnsavedChanges = !!pendingReorders[target.id];
    const selectedCount = getSelectedCount(target.id);
    const targetSelected = selectedContent[target.id] || new Set();
    
    if (allContent.length === 0) {
      return (
        <div className="text-center py-4 text-muted-foreground text-sm">
          No content yet. Add videos, documents, or links.
        </div>
      );
    }
    
    return (
      <div className="space-y-2">
        {/* Multi-select controls */}
        <div className="flex items-center justify-between bg-slate-50 border rounded-lg px-3 py-2">
          <div className="flex items-center gap-3">
            <Checkbox
              id={`select-all-${target.id}`}
              checked={selectedCount === allContent.length && allContent.length > 0}
              onCheckedChange={() => toggleSelectAll(target, allContent)}
              data-testid={`select-all-${target.id}`}
            />
            <label htmlFor={`select-all-${target.id}`} className="text-sm text-muted-foreground cursor-pointer">
              {selectedCount > 0 ? `${selectedCount} selected` : 'Select all'}
            </label>
          </div>
          {selectedCount > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleDeleteSelected(target, allContent)}
              disabled={deletingSelected}
              data-testid={`delete-selected-${target.id}`}
            >
              {deletingSelected ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <Trash2 className="h-3 w-3 mr-1" />
              )}
              Delete Selected
            </Button>
          )}
        </div>
        
        {hasUnsavedChanges && (
          <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded flex items-center gap-1">
            <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
            Order changed - click Save to apply
          </div>
        )}
        {allContent.map((item, index) => (
          <div key={item.id} className={`flex items-center justify-between p-2 rounded-lg group ${targetSelected.has(item.id) ? 'bg-blue-50 border border-blue-200' : hasUnsavedChanges ? 'bg-amber-50/50 border border-amber-200' : 'bg-muted/50'}`}>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {/* Checkbox for selection */}
              <Checkbox
                checked={targetSelected.has(item.id)}
                onCheckedChange={() => toggleContentSelection(target.id, item.id)}
                data-testid={`select-content-${item.id}`}
              />
              
              {/* Reorder buttons */}
              <div className="flex flex-col gap-0.5 opacity-50 group-hover:opacity-100">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 p-0"
                  onClick={() => moveContentUp(target, item, allContent)}
                  disabled={index === 0}
                >
                  <ChevronDown className="h-3 w-3 rotate-180" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 p-0"
                  onClick={() => moveContentDown(target, item, allContent)}
                  disabled={index === allContent.length - 1}
                >
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </div>
              
              {/* Content type icon */}
              {item.contentType === 'video' && <Video className="h-4 w-4 text-blue-500 flex-shrink-0" />}
              {item.contentType === 'document' && <File className="h-4 w-4 text-orange-500 flex-shrink-0" />}
              {item.contentType === 'onedrive_link' && <ExternalLink className="h-4 w-4 text-purple-500 flex-shrink-0" />}
              
              <span className="text-sm truncate">{item.title}</span>
              
              {/* Type badges */}
              {item.contentType === 'video' && (
                <>
                  <Badge variant="outline" className="text-xs flex-shrink-0">{item.type === 'video_upload' ? 'Video' : 'URL'}</Badge>
                  {item.transcript && <Badge className="bg-emerald-500 text-xs flex-shrink-0">Transcribed</Badge>}
                </>
              )}
              {item.contentType === 'document' && (
                <Badge variant="outline" className="text-xs flex-shrink-0">{item.file_type?.toUpperCase()}</Badge>
              )}
              {item.contentType === 'onedrive_link' && (
                <>
                  <Badge variant="outline" className="text-xs flex-shrink-0">OneDrive</Badge>
                  {item.transcript && <Badge className="bg-emerald-500 text-xs flex-shrink-0">Transcribed</Badge>}
                </>
              )}
              
              <span className="text-xs text-muted-foreground ml-auto mr-2">#{index + 1}</span>
            </div>
            
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Transcript button for ALL videos (uploaded or URL-based) */}
              {item.contentType === 'video' && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => handleOpenTranscriptDialog(item, target.id, target.module_id ? 'submodule' : 'module')}
                  title={item.transcript ? "Edit Transcript" : "Add Transcript"}
                >
                  {item.transcript ? (
                    <Edit className="h-3 w-3 text-emerald-600" />
                  ) : (
                    <FileText className="h-3 w-3" />
                  )}
                </Button>
              )}
              {/* Transcript button for OneDrive links */}
              {item.contentType === 'onedrive_link' && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => handleOpenTranscriptDialog(item, target.id, target.module_id ? 'submodule' : 'module')}
                  title={item.transcript ? "Edit Transcript" : "Add Transcript"}
                >
                  {item.transcript ? (
                    <Edit className="h-3 w-3 text-emerald-600" />
                  ) : (
                    <FileText className="h-3 w-3" />
                  )}
                </Button>
              )}
              {/* Auto-transcribe button ONLY for uploaded videos without transcript */}
              {item.contentType === 'video' && item.type === 'video_upload' && !item.transcript && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => handleTranscribeVideo(item, target)}
                  disabled={transcribing === item.id}
                  title="Auto-Extract Transcript"
                >
                  {transcribing === item.id ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <FileAudio className="h-3 w-3" />
                  )}
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-destructive"
                onClick={() => handleDeleteContent(item.contentType, item.id, target)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  if (!course) {
    return (
      <MainLayout>
        <div className="text-center py-16">
          <h2 className="text-xl font-semibold">Course not found</h2>
          <Button asChild className="mt-4">
            <Link to="/admin/course-builder">Back to Courses</Link>
          </Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="course-editor">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/admin/course-builder">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{course.title}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={course.status === 'published' ? 'default' : 'secondary'}>
                  {course.status}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {course.category_name}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {course.status === 'published' && (
              <Button variant="outline" onClick={handleOpenAssignDialog} data-testid="open-assign-users-btn">
                <Users className="h-4 w-4 mr-2" />
                Assign Users
              </Button>
            )}
            <Button variant="outline" asChild>
              <Link to={`/admin/course-builder/${courseId}/preview`}>
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Link>
            </Button>
            <Button variant="outline" onClick={handleSaveCourse} disabled={saving} className={Object.keys(pendingReorders).length > 0 ? 'border-amber-500 bg-amber-50 text-amber-700 hover:bg-amber-100' : ''}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save {Object.keys(pendingReorders).length > 0 && '(unsaved changes)'}
            </Button>
            <Button onClick={handlePublish}>
              {course.status === 'published' ? (
                <>
                  <GlobeLock className="h-4 w-4 mr-2" />
                  Unpublish
                </>
              ) : (
                <>
                  <Globe className="h-4 w-4 mr-2" />
                  Publish
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{course.module_count || 0}</p>
              <p className="text-xs text-muted-foreground">Modules</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{course.total_sub_modules || 0}</p>
              <p className="text-xs text-muted-foreground">Sub-Modules</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{course.total_videos || 0}</p>
              <p className="text-xs text-muted-foreground">Videos</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{course.total_documents || 0}</p>
              <p className="text-xs text-muted-foreground">Documents</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{course.total_assessments || 0}</p>
              <p className="text-xs text-muted-foreground">Assessments</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="structure">Course Structure</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="reset-progress" data-testid="course-reset-progress-tab">Reset Progress</TabsTrigger>
          </TabsList>

          <TabsContent value="reset-progress" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Reset Learner Progress</CardTitle>
                <CardDescription>Reset progress for assigned users by department — the whole course or selected modules / sub-modules</CardDescription>
              </CardHeader>
              <CardContent>
                <ProgressResetTool api={api} source={'tech-learning'} courseId={courseId} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="structure" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Modules & Content</CardTitle>
                  <CardDescription>Build your course structure</CardDescription>
                </div>
                <Button onClick={() => { setEditingModule(null); setModuleForm({ title: '', description: '', has_sub_modules: true }); setShowModuleDialog(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Module
                </Button>
              </CardHeader>
              <CardContent>
                {course.modules?.length === 0 ? (
                  <div className="text-center py-12">
                    <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-medium">No modules yet</h3>
                    <p className="text-sm text-muted-foreground mt-1">Add your first module to start building the course</p>
                  </div>
                ) : (
                  <Accordion 
                    type="multiple" 
                    className="space-y-2"
                    value={expandedModules}
                    onValueChange={setExpandedModules}
                  >
                    {course.modules?.map((module, idx) => (
                      <AccordionItem key={module.id} value={module.id} className="border rounded-lg px-4">
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-3 flex-1">
                            <BookOpen className="h-5 w-5 text-primary" />
                            <div className="text-left">
                              <p className="font-medium">{module.title}</p>
                              <p className="text-xs text-muted-foreground">
                                {module.has_sub_modules 
                                  ? `${module.sub_module_count || 0} sub-modules`
                                  : `Direct content`
                                }
                                {module.has_assessment && ' • Has assessment'}
                              </p>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2 pb-4">
                          {/* Module Actions */}
                          <div className="flex items-center gap-2 mb-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingModule(module);
                                setModuleForm({ title: module.title, description: module.description || '', has_sub_modules: module.has_sub_modules });
                                setShowModuleDialog(true);
                              }}
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                            {module.has_sub_modules && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedTarget(module);
                                  setShowSubModuleDialog(true);
                                }}
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Add Sub-Module
                              </Button>
                            )}
                            {!module.has_sub_modules && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedTarget(module);
                                    setShowContentDialog(true);
                                  }}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Add Content
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleOpenQuizOptions(module)}
                                  disabled={generatingQuiz}
                                  data-testid={`module-generate-quiz-btn-${module.id}`}
                                >
                                  {generatingQuiz ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                                  {module.has_assessment ? 'Regenerate Quiz' : 'Generate Quiz'}
                                </Button>
                                {module.has_assessment && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleOpenEditQuiz(module)}
                                    data-testid={`module-edit-quiz-btn-${module.id}`}
                                  >
                                    <Edit className="h-3 w-3 mr-1" />
                                    Edit Quiz
                                  </Button>
                                )}
                              </>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive ml-auto"
                              onClick={() => handleDeleteModule(module.id)}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Delete
                            </Button>
                          </div>

                          {/* Sub-modules or Direct Content */}
                          {module.has_sub_modules ? (
                            <div className="space-y-3 pl-4 border-l-2 border-muted">
                              {module.sub_modules?.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-2">No sub-modules yet</p>
                              ) : (
                                module.sub_modules?.map(subModule => (
                                  <Card key={subModule.id} className="bg-muted/30">
                                    <CardContent className="p-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <div>
                                          <p className="font-medium">{subModule.title}</p>
                                          <p className="text-xs text-muted-foreground">
                                            {subModule.description || 'No description'}
                                            {subModule.has_assessment && ' • Has assessment'}
                                          </p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={() => {
                                              setSelectedTarget(subModule);
                                              setShowContentDialog(true);
                                            }}
                                            data-testid={`submodule-add-content-btn-${subModule.id}`}
                                            title="Add Content"
                                          >
                                            <Plus className="h-3 w-3" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={() => handleOpenQuizOptions(subModule)}
                                            disabled={generatingQuiz}
                                            data-testid={`submodule-generate-quiz-btn-${subModule.id}`}
                                            title={subModule.has_assessment ? 'Regenerate Quiz' : 'Generate Quiz'}
                                          >
                                            <Sparkles className="h-3 w-3" />
                                          </Button>
                                          {subModule.has_assessment && (
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-7 w-7"
                                              onClick={() => handleOpenEditQuiz(subModule)}
                                              data-testid={`submodule-edit-quiz-btn-${subModule.id}`}
                                              title="Edit Quiz"
                                            >
                                              <Edit className="h-3 w-3" />
                                            </Button>
                                          )}
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 text-destructive"
                                            onClick={() => handleDeleteSubModule(subModule.id)}
                                          >
                                            <Trash2 className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      </div>
                                      {renderContentList(subModule)}
                                    </CardContent>
                                  </Card>
                                ))
                              )}
                            </div>
                          ) : (
                            <div className="pl-4">
                              {renderContentList(module)}
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Course Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Course Title</label>
                  <Input
                    value={course.title}
                    onChange={(e) => setCourse({ ...course, title: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Description</label>
                  <Textarea
                    value={course.description || ''}
                    onChange={(e) => setCourse({ ...course, description: e.target.value })}
                    className="mt-1"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Category</label>
                  <Select 
                    value={course.category_id} 
                    onValueChange={(v) => setCourse({ ...course, category_id: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Passing Score (%)</label>
                  <Input
                    type="number"
                    min={50}
                    max={100}
                    value={course.passing_score}
                    onChange={(e) => setCourse({ ...course, passing_score: parseInt(e.target.value) || 75 })}
                    className="mt-1 w-32"
                  />
                </div>
                <Button onClick={handleSaveCourse} disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Save Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Hidden File Inputs */}
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="video/*"
          onChange={handleVideoUpload}
        />
        <input
          type="file"
          ref={docInputRef}
          className="hidden"
          accept=".pdf,.doc,.docx"
          multiple
          onChange={handleDocumentUpload}
        />

        {/* Module Dialog */}
        <Dialog open={showModuleDialog} onOpenChange={setShowModuleDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingModule ? 'Edit Module' : 'Add Module'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium">Module Title *</label>
                <Input
                  value={moduleForm.title}
                  onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                  placeholder="e.g., Getting Started"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <Textarea
                  value={moduleForm.description}
                  onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                  placeholder="Brief description..."
                  className="mt-1"
                  rows={2}
                />
              </div>
              {!editingModule && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="has_sub_modules"
                    checked={moduleForm.has_sub_modules}
                    onChange={(e) => setModuleForm({ ...moduleForm, has_sub_modules: e.target.checked })}
                    className="rounded"
                  />
                  <label htmlFor="has_sub_modules" className="text-sm">
                    Has sub-modules (recommended for larger modules)
                  </label>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModuleDialog(false)}>Cancel</Button>
              <Button onClick={editingModule ? handleUpdateModule : handleCreateModule}>
                {editingModule ? 'Update' : 'Create'} Module
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Sub-Module Dialog */}
        <Dialog open={showSubModuleDialog} onOpenChange={setShowSubModuleDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Sub-Module</DialogTitle>
              <DialogDescription>
                Adding to: {selectedTarget?.title}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium">Sub-Module Title *</label>
                <Input
                  value={subModuleForm.title}
                  onChange={(e) => setSubModuleForm({ ...subModuleForm, title: e.target.value })}
                  placeholder="e.g., Installation Guide"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <Textarea
                  value={subModuleForm.description}
                  onChange={(e) => setSubModuleForm({ ...subModuleForm, description: e.target.value })}
                  placeholder="Brief description..."
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSubModuleDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateSubModule}>Create Sub-Module</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Content Dialog */}
        <Dialog open={showContentDialog} onOpenChange={setShowContentDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Content</DialogTitle>
              <DialogDescription>
                Adding to: {selectedTarget?.title}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant="outline"
                  className="flex flex-col h-20 gap-1"
                  onClick={() => {
                    setSelectedTarget(selectedTarget);
                    fileInputRef.current?.click();
                  }}
                  disabled={uploading}
                >
                  <Upload className="h-5 w-5" />
                  <span className="text-xs">Upload Video</span>
                </Button>
                <Button
                  variant="outline"
                  className="flex flex-col h-20 gap-1"
                  onClick={() => {
                    setSelectedTarget(selectedTarget);
                    docInputRef.current?.click();
                  }}
                  disabled={uploading}
                >
                  <FileText className="h-5 w-5" />
                  <span className="text-xs">Upload Docs</span>
                </Button>
                <Button
                  variant="outline"
                  className="flex flex-col h-20 gap-1 bg-muted"
                  disabled
                >
                  <span className="text-xs text-muted-foreground">or add URL below</span>
                </Button>
              </div>
              
              {uploading && (
                <div className="flex items-center justify-center gap-2 py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Uploading...</span>
                </div>
              )}
              
              <div className="border-t pt-4">
                <label className="text-sm font-medium">Content Type</label>
                <Select value={contentForm.type} onValueChange={(v) => setContentForm({ ...contentForm, type: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video_url">Video URL (YouTube, Vimeo, etc.)</SelectItem>
                    <SelectItem value="onedrive">OneDrive Link</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">URL *</label>
                <Input
                  value={contentForm.url}
                  onChange={(e) => setContentForm({ ...contentForm, url: e.target.value })}
                  placeholder={contentForm.type === 'video_url' ? 'https://youtube.com/...' : 'https://...sharepoint.com/_layouts/15/embed.aspx?UniqueId=...'}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Title *</label>
                <Input
                  value={contentForm.title}
                  onChange={(e) => setContentForm({ ...contentForm, title: e.target.value })}
                  placeholder="Content title"
                  className="mt-1"
                />
              </div>
              {/* Transcript for Video URLs */}
              {contentForm.type === 'video_url' && (
                <div>
                  <label className="text-sm font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Video Transcript (Optional)
                  </label>
                  <Textarea
                    value={contentForm.transcript}
                    onChange={(e) => setContentForm({ ...contentForm, transcript: e.target.value })}
                    placeholder="Paste the video transcript here to enable AI-powered quiz generation..."
                    className="mt-1 min-h-[100px]"
                    data-testid="video-url-transcript-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Adding a transcript helps the AI generate better quiz questions from this video.
                  </p>
                </div>
              )}
              {/* OneDrive specific fields */}
              {contentForm.type === 'onedrive' && (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                    <p className="font-medium text-blue-800 mb-1">📌 For best results with OneDrive/SharePoint:</p>
                    <ol className="text-blue-700 text-xs space-y-1 ml-4 list-decimal">
                      <li>Open the file in SharePoint/OneDrive</li>
                      <li>Click <strong>Share</strong> → <strong>Embed</strong></li>
                      <li>Copy the <strong>src</strong> URL from the iframe code</li>
                      <li>Paste the embed URL below (starts with <code className="bg-blue-100 px-1 rounded">https://...sharepoint.com/_layouts/15/embed.aspx</code>)</li>
                    </ol>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Description</label>
                    <Input
                      value={contentForm.description}
                      onChange={(e) => setContentForm({ ...contentForm, description: e.target.value })}
                      placeholder="Optional description"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Transcript (Optional)
                    </label>
                    <Textarea
                      value={contentForm.transcript}
                      onChange={(e) => setContentForm({ ...contentForm, transcript: e.target.value })}
                      placeholder="Paste the video/document transcript here to enable AI-powered quiz generation..."
                      className="mt-1 min-h-[100px]"
                      data-testid="onedrive-transcript-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Adding a transcript helps the AI generate better quiz questions from this content.
                    </p>
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowContentDialog(false)}>Cancel</Button>
              <Button onClick={handleAddContent}>Add Content</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Quiz Preview Dialog */}
        <Dialog open={showQuizPreview} onOpenChange={setShowQuizPreview}>
          <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="quiz-preview-dialog">
            <DialogHeader>
              <DialogTitle>Generated Assessment</DialogTitle>
              <DialogDescription>
                {quizPreviewData?.questions_generated} questions generated from {quizPreviewData?.source_content?.videos || 0} videos and {quizPreviewData?.source_content?.documents || 0} documents
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="max-h-[50vh] pr-4">
              <div className="space-y-4 py-4">
                {quizPreviewData?.questions?.map((q, idx) => (
                  <Card key={q.id} data-testid={`quiz-preview-question-${idx}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <Badge variant="outline" data-testid={`quiz-preview-question-type-${idx}`}>{q.type.toUpperCase()}</Badge>
                        <Badge variant={q.difficulty === 'easy' ? 'secondary' : q.difficulty === 'hard' ? 'destructive' : 'default'}>
                          {q.difficulty}
                        </Badge>
                      </div>
                      <p className="font-medium">{idx + 1}. {q.question}</p>
                      {q.options && (
                        <ul className="mt-2 space-y-1">
                          {q.options.map(opt => (
                            <li key={opt.id} className={`text-sm pl-4 ${opt.is_correct ? 'text-emerald-600 font-medium' : ''}`}>
                              {opt.is_correct && <Check className="inline h-3 w-3 mr-1" />}
                              {opt.text}
                            </li>
                          ))}
                        </ul>
                      )}
                      {q.type === 'true_false' && q.correct_answer && (
                        <p className="text-sm mt-2 text-emerald-600">
                          Answer: {q.correct_answer}
                        </p>
                      )}
                      {q.type === 'short_answer' && (
                        <div className="mt-2 space-y-1">
                          {q.keywords && q.keywords.length > 0 && (
                            <p className="text-xs text-muted-foreground">
                              Keywords: {q.keywords.join(', ')}
                            </p>
                          )}
                          {q.sample_answer && (
                            <p className="text-sm text-emerald-600">
                              Sample Answer: {q.sample_answer}
                            </p>
                          )}
                        </div>
                      )}
                      {q.code_template && (
                        <pre className="text-xs bg-muted p-2 rounded mt-2 overflow-x-auto">
                          {q.code_template}
                        </pre>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setShowQuizPreview(false)} data-testid="quiz-preview-close-btn">Close</Button>
              <Button 
                onClick={handlePublishQuiz} 
                disabled={publishingQuiz}
                data-testid="quiz-publish-btn"
              >
                {publishingQuiz ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                Publish Quiz
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Quiz Options Dialog */}
        <Dialog open={showQuizOptionsDialog} onOpenChange={setShowQuizOptionsDialog}>
          <DialogContent className="max-w-md" data-testid="quiz-options-dialog">
            <DialogHeader>
              <DialogTitle>Generate Quiz</DialogTitle>
              <DialogDescription>
                Configure quiz generation for: {quizTarget?.title}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Number of Questions */}
              <div>
                <label className="text-sm font-medium">Number of Questions</label>
                <Input
                  type="number"
                  min={3}
                  max={15}
                  value={quizOptions.num_questions}
                  onChange={(e) => setQuizOptions({ ...quizOptions, num_questions: parseInt(e.target.value) || 7 })}
                  className="mt-1 w-24"
                  data-testid="quiz-num-questions-input"
                />
              </div>
              
              {/* Question Types */}
              <div>
                <label className="text-sm font-medium mb-2 block">Question Types</label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="include_mcq"
                      data-testid="quiz-include-mcq-checkbox"
                      checked={quizOptions.include_mcq}
                      onCheckedChange={(checked) => setQuizOptions({ ...quizOptions, include_mcq: checked })}
                    />
                    <label htmlFor="include_mcq" className="text-sm">Multiple Choice</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="include_true_false"
                      data-testid="quiz-include-true-false-checkbox"
                      checked={quizOptions.include_true_false}
                      onCheckedChange={(checked) => setQuizOptions({ ...quizOptions, include_true_false: checked })}
                    />
                    <label htmlFor="include_true_false" className="text-sm">True/False</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="include_short_answer"
                      data-testid="quiz-include-short-answer-checkbox"
                      checked={quizOptions.include_short_answer}
                      onCheckedChange={(checked) => setQuizOptions({ ...quizOptions, include_short_answer: checked })}
                    />
                    <label htmlFor="include_short_answer" className="text-sm">Short Answer</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="include_coding"
                      data-testid="quiz-include-coding-checkbox"
                      checked={quizOptions.include_coding}
                      onCheckedChange={(checked) => setQuizOptions({ ...quizOptions, include_coding: checked })}
                    />
                    <label htmlFor="include_coding" className="text-sm">Coding Challenges (if applicable)</label>
                  </div>
                </div>
              </div>
              
              {/* Difficulty Mix */}
              <div>
                <label className="text-sm font-medium mb-2 block">Difficulty Distribution</label>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs text-muted-foreground">Easy</label>
                    <Input
                      type="number"
                      min={0}
                      max={10}
                      value={quizOptions.difficulty_mix.easy}
                      onChange={(e) => setQuizOptions({
                        ...quizOptions,
                        difficulty_mix: { ...quizOptions.difficulty_mix, easy: parseInt(e.target.value) || 0 }
                      })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Medium</label>
                    <Input
                      type="number"
                      min={0}
                      max={10}
                      value={quizOptions.difficulty_mix.medium}
                      onChange={(e) => setQuizOptions({
                        ...quizOptions,
                        difficulty_mix: { ...quizOptions.difficulty_mix, medium: parseInt(e.target.value) || 0 }
                      })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Hard</label>
                    <Input
                      type="number"
                      min={0}
                      max={10}
                      value={quizOptions.difficulty_mix.hard}
                      onChange={(e) => setQuizOptions({
                        ...quizOptions,
                        difficulty_mix: { ...quizOptions.difficulty_mix, hard: parseInt(e.target.value) || 0 }
                      })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowQuizOptionsDialog(false)} data-testid="quiz-options-cancel-btn">Cancel</Button>
              <Button onClick={handleGenerateQuiz} disabled={generatingQuiz} data-testid="quiz-options-generate-btn">
                {generatingQuiz ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Generate Quiz
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* User Assignment Dialog (department filter, pick users, assign everyone in departments) */}
        <AssignUsersDialog
          open={showAssignDialog}
          onOpenChange={setShowAssignDialog}
          builderBase="/tech-learning/builder"
          courseId={courseId}
          onChanged={() => fetchCourse({ silent: true })}
        />

        {/* Edit Quiz Dialog */}
        <Dialog open={showEditQuizDialog} onOpenChange={setShowEditQuizDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Edit Quiz Questions</DialogTitle>
              <DialogDescription>
                Edit the quiz questions below. Changes will be saved when you click "Save Changes".
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto min-h-0 pr-2" style={{ maxHeight: 'calc(90vh - 180px)' }}>
              <div className="space-y-6 py-4">
                {editingQuestions.map((question, qIndex) => (
                  <Card key={qIndex} className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <Badge variant="outline" className="mb-2">
                        {question.type === 'mcq' && 'Multiple Choice'}
                        {question.type === 'true_false' && 'True/False'}
                        {question.type === 'short_answer' && 'Short Answer'}
                        {question.type === 'coding' && 'Coding Challenge'}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => deleteQuestion(qIndex)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm font-medium">Question</Label>
                        <textarea
                          value={question.question}
                          onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                          className="w-full mt-1 p-2 border rounded-md text-sm min-h-[60px] resize-y"
                        />
                      </div>
                      
                      {/* MCQ Options */}
                      {question.type === 'mcq' && question.options && (
                        <div>
                          <Label className="text-sm font-medium">Options</Label>
                          <div className="space-y-2 mt-1">
                            {question.options.map((opt, optIndex) => (
                              <div key={optIndex} className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  checked={opt.is_correct}
                                  onChange={() => {
                                    // Update correct answer
                                    const updated = [...editingQuestions];
                                    updated[qIndex].options = updated[qIndex].options.map((o, i) => ({
                                      ...o,
                                      is_correct: i === optIndex
                                    }));
                                    setEditingQuestions(updated);
                                  }}
                                  className="w-4 h-4"
                                />
                                <Input
                                  value={opt.text}
                                  onChange={(e) => updateQuestionOption(qIndex, optIndex, 'text', e.target.value)}
                                  className="flex-1"
                                  placeholder={`Option ${optIndex + 1}`}
                                />
                                {opt.is_correct && (
                                  <Badge className="bg-green-500">Correct</Badge>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* True/False Correct Answer */}
                      {question.type === 'true_false' && (
                        <div>
                          <Label className="text-sm font-medium">Correct Answer</Label>
                          <div className="flex gap-4 mt-1">
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                checked={question.correct_answer === true}
                                onChange={() => updateQuestion(qIndex, 'correct_answer', true)}
                              />
                              True
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                checked={question.correct_answer === false}
                                onChange={() => updateQuestion(qIndex, 'correct_answer', false)}
                              />
                              False
                            </label>
                          </div>
                        </div>
                      )}
                      
                      {/* Short Answer Expected */}
                      {question.type === 'short_answer' && (
                        <div>
                          <Label className="text-sm font-medium">Expected Answer (for grading reference)</Label>
                          <textarea
                            value={question.expected_answer || ''}
                            onChange={(e) => updateQuestion(qIndex, 'expected_answer', e.target.value)}
                            className="w-full mt-1 p-2 border rounded-md text-sm min-h-[60px] resize-y"
                          />
                        </div>
                      )}
                      
                      {/* Coding Challenge */}
                      {question.type === 'coding' && (
                        <div className="space-y-3">
                          <div>
                            <Label className="text-sm font-medium">Starter Code</Label>
                            <textarea
                              value={question.starter_code || ''}
                              onChange={(e) => updateQuestion(qIndex, 'starter_code', e.target.value)}
                              className="w-full mt-1 p-2 border rounded-md text-sm font-mono min-h-[80px] resize-y bg-gray-50"
                              placeholder="# Starter code for students"
                            />
                          </div>
                          <div>
                            <Label className="text-sm font-medium">Expected Output</Label>
                            <textarea
                              value={question.expected_output || ''}
                              onChange={(e) => updateQuestion(qIndex, 'expected_output', e.target.value)}
                              className="w-full mt-1 p-2 border rounded-md text-sm font-mono min-h-[60px] resize-y bg-gray-50"
                              placeholder="Expected program output"
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* Explanation */}
                      <div>
                        <Label className="text-sm font-medium">Explanation (shown after answer)</Label>
                        <textarea
                          value={question.explanation || ''}
                          onChange={(e) => updateQuestion(qIndex, 'explanation', e.target.value)}
                          className="w-full mt-1 p-2 border rounded-md text-sm min-h-[40px] resize-y"
                          placeholder="Why this is the correct answer..."
                        />
                      </div>
                    </div>
                  </Card>
                ))}
                
                {editingQuestions.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No questions in this quiz
                  </div>
                )}
              </div>
            </div>
            
            <DialogFooter className="border-t pt-4 flex-shrink-0">
              <Button variant="outline" onClick={() => setShowEditQuizDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveQuiz} disabled={savingQuiz}>
                {savingQuiz ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Transcript Dialog */}
        <Dialog open={showTranscriptDialog} onOpenChange={setShowTranscriptDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Video Transcript</DialogTitle>
              <DialogDescription>
                {editingVideo?.title && `Add or edit the transcript for "${editingVideo.title}"`}
                <br />
                <span className="text-xs text-muted-foreground">
                  The transcript will be used by AI to generate relevant quiz questions.
                </span>
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto min-h-0 py-4" style={{ maxHeight: 'calc(90vh - 200px)' }}>
              <Textarea
                value={transcriptText}
                onChange={(e) => setTranscriptText(e.target.value)}
                placeholder="Paste or type the video transcript here...

Example:
Welcome to this tutorial on Python basics. In this video, we'll cover variables, data types, and basic operations. 

First, let's talk about variables. A variable is a container for storing data values..."
                className="min-h-[300px] resize-y font-mono text-sm"
              />
              <div className="mt-2 text-xs text-muted-foreground">
                {transcriptText.length > 0 && (
                  <span>{transcriptText.split(/\s+/).filter(Boolean).length} words</span>
                )}
              </div>
            </div>
            
            <DialogFooter className="border-t pt-4 flex-shrink-0">
              <Button variant="outline" onClick={() => {
                setShowTranscriptDialog(false);
                setEditingVideo(null);
                setTranscriptText('');
              }}>
                Cancel
              </Button>
              <Button onClick={handleSaveTranscript} disabled={savingTranscript}>
                {savingTranscript ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Transcript
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
